import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { DASHBOARD_LOADING_LABEL } from '@/lib/translations';

export interface ReadingSkeletonProps {
  /** Which reading's first screen to sketch: Orçamento's bucket rows or Análise's two breakdowns. */
  variant?: 'budget' | 'analysis';
}

const FigurePlaceholder: React.FC<{ className?: string }> = ({ className }) => (
  <div className={className}>
    <Skeleton className="mb-2 h-3 w-24" />
    <Skeleton className="h-8 w-36" />
  </div>
);

const RowPlaceholder: React.FC = () => (
  <li className="space-y-3 py-4">
    <div className="flex items-center justify-between gap-6">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-20" />
    </div>
    <Skeleton className="h-1.5 w-full rounded-full" />
  </li>
);

const BreakdownPlaceholder: React.FC = () => (
  <div>
    <Skeleton className="mb-5 h-6 w-44" />
    <div className="flex flex-col items-center gap-6 lg:flex-row">
      <Skeleton className="h-[200px] w-[200px] shrink-0 rounded-full" />
      <div className="w-full flex-1 space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  </div>
);

// Sketches the reading's own first screen, so what arrives replaces it without moving the page. Skeletons,
// not a centered spinner (PRODUCT.md, Operate): the front door shows its structure before its numbers.
export const ReadingSkeleton: React.FC<ReadingSkeletonProps> = ({ variant = 'budget' }) => (
  <div role="status" className="space-y-8">
    <span className="sr-only">{DASHBOARD_LOADING_LABEL}</span>
    <div aria-hidden="true" className="space-y-8">
      <div className="grid grid-cols-2 gap-x-10 gap-y-6 border-b border-border/60 pb-6 sm:grid-cols-3 sm:gap-x-12">
        <FigurePlaceholder />
        <FigurePlaceholder />
        <FigurePlaceholder className="col-span-2 sm:col-span-1" />
      </div>
      {variant === 'budget' ? (
        <ul className="divide-y divide-border/50">
          {Array.from({ length: 6 }, (_, index) => (
            <RowPlaceholder key={index} />
          ))}
        </ul>
      ) : (
        <div className="grid gap-12 lg:grid-cols-2">
          <BreakdownPlaceholder />
          <BreakdownPlaceholder />
        </div>
      )}
    </div>
  </div>
);
